import { Temporal } from '@js-temporal/polyfill'

// Exemplo: Temporal.PlainDate.from('2024-01-15') = apenas 15 de janeiro de 2024
export function formatPlainDateTimeAgo (plainDate: Temporal.PlainDate): string {
  const formatter = new Intl.RelativeTimeFormat('pt-BR', { style: 'long' })
  const now = Temporal.Now.plainDateISO() // Data atual sem horário

  // Calcula a diferença em dias
  const diffInDays = now.until(plainDate).total('days')
  const absDiffInDays = Math.abs(diffInDays)

  if (absDiffInDays >= 7) {
    // Se for mais de uma semana, retorna apenas o dia e mês
    return plainDate.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit'
    })
  } else if (absDiffInDays >= 1) {
    return formatter.format(-Math.floor(absDiffInDays), 'days')
  }

  return formatter.format(-Math.floor(absDiffInDays), 'days')
}

// Função utilitária para criar PlainDate a partir de string que pode conter timezone
// Remove timezone designators (Z, +XX:XX, -XX:XX) antes de criar o PlainDate
export function createPlainDateFromString (dateString: string): Temporal.PlainDate {
  // Remove timezone designators comuns
  const cleanDateString = dateString
    .replace(/Z$/, '') // Remove Z do final
    .replace(/[+-]\d{2}:\d{2}$/, '') // Remove +XX:XX ou -XX:XX do final
    .split('T')[0] // Pega apenas a parte da data (antes do T)

  return Temporal.PlainDate.from(cleanDateString)
}

/**
 * Converte Temporal.PlainDate para Date nativo.
 * Útil para props min-date/max-date de componentes que esperam Date.
 */
export function temporalToDate (date: Temporal.PlainDate | undefined): Date | undefined {
  if (!date) {
    return undefined
  }
  return new Date(date.year, date.month - 1, date.day)
}

// Função utilitária para criar PlainDate com verificação de nulo/undefined
export function createPlainDateSafe (dateString: string | null | undefined): Temporal.PlainDate | undefined {
  if (dateString == null) {
    return undefined
  }
  return createPlainDateFromString(dateString)
}

/**
 * Normalizes date-like values to an ISO date string (YYYY-MM-DD) for API query params.
 */
export function toIsoDateString (value: unknown): string | undefined {
  if (value == null || value === '') {
    return undefined
  }

  if (typeof value === 'string') {
    return value.split('T')[0]
  }

  if (value instanceof Date) {
    return value.toISOString().split('T')[0]
  }

  if (value instanceof Temporal.PlainDate) {
    return value.toString()
  }

  if (value instanceof Temporal.PlainDateTime) {
    return value.toPlainDate().toString()
  }

  return undefined
}

export function formatPlainDateTimeSecondsRemaining (plainDateTime: Temporal.PlainDateTime): number {
  const now = Temporal.Now.plainDateTimeISO()
  const diffInSeconds = now.until(plainDateTime).total('seconds')

  return Math.floor(diffInSeconds)
}

// Função utilitária para criar PlainDateTime a partir de string que pode conter timezone
// Normaliza formatos comuns do backend (espaço como separador, timezone designators)
export function createPlainDateTimeFromString (dateString: string): Temporal.PlainDateTime {
  const normalized = dateString
    .replace(' ', 'T')
    .replace(/Z$/, '')
    .replace(/[+-]\d{2}:\d{2}$/, '')

  return Temporal.PlainDateTime.from(normalized)
}

// Função utilitária para criar PlainDateTime com verificação de nulo/undefined
export function createPlainDateTimeSafe (dateString: string | null | undefined): Temporal.PlainDateTime | undefined {
  if (dateString == null) {
    return undefined
  }
  return Temporal.PlainDateTime.from(dateString.replace(/Z$/, ''))
}

export function createInstantSafe (dateString: string | null | undefined): Temporal.Instant | undefined {
  if (dateString == null) {
    return undefined
  }

  try {
    return Temporal.Instant.from(dateString)
  } catch {
    return undefined
  }
}

/**
 * Formats PlainDateTime for API DATE_ATOM (Y-m-d\TH:i:sP): no fractional seconds, local timezone offset.
 */
export function formatPlainDateTimeForAPI (dateTime: Temporal.PlainDateTime): string {
  const normalized = dateTime.with({
    millisecond: 0,
    microsecond: 0,
    nanosecond: 0
  })

  return normalized
    .toZonedDateTime(Temporal.Now.timeZoneId())
    .toString({ timeZoneName: 'never' })
}

export function createPlainDateTimeFromInstantSafe (
  dateString: string | null | undefined,
  timeZoneId: string = Temporal.Now.timeZoneId()
): Temporal.PlainDateTime | undefined {
  if (dateString == null) {
    return undefined
  }

  try {
    return Temporal.Instant.from(dateString)
      .toZonedDateTimeISO(timeZoneId)
      .toPlainDateTime()
  } catch {
    return Temporal.PlainDateTime.from(dateString)
  }
}

export type DatePickerView = 'date' | 'month' | 'year'

export interface NormalizePlainDateForViewOptions {
  isEndDate?: boolean
}

/**
 * Normalizes a PlainDate based on the date picker view.
 * - year: start = 2024-01-01, end = 2024-12-31
 * - month: start = day 1 (e.g. 2024-03-01), end = last day of month (e.g. 2024-03-31)
 * - date: returns value unchanged
 */
export function normalizePlainDateForView (
  value: Temporal.PlainDate,
  view: DatePickerView,
  options?: NormalizePlainDateForViewOptions
): Temporal.PlainDate {
  const isEndDate = options?.isEndDate ?? false

  if (view === 'year') {
    return isEndDate
      ? new Temporal.PlainDate(value.year, 12, 31)
      : new Temporal.PlainDate(value.year, 1, 1)
  }
  if (view === 'month') {
    if (isEndDate) {
      const yearMonth = Temporal.PlainYearMonth.from({ year: value.year, month: value.month })
      const lastDay = yearMonth.daysInMonth
      return new Temporal.PlainDate(value.year, value.month, lastDay)
    }
    return new Temporal.PlainDate(value.year, value.month, 1)
  }
  return value
}

export function formatInstantLocaleDateTime (iso: string, locale = 'pt-BR'): string | null {
  try {
    const zoned = Temporal.Instant.from(iso).toZonedDateTimeISO(Temporal.Now.timeZoneId())

    return zoned.toLocaleString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  } catch {
    return null
  }
}

export interface PlainDateRange {
  begin: Temporal.PlainDate
  until: Temporal.PlainDate
}

/**
 * Returns the Monday of the ISO week containing the given date.
 * Temporal dayOfWeek: 1 = Monday, 7 = Sunday.
 */
export function getStartOfWeek (date: Temporal.PlainDate): Temporal.PlainDate {
  return date.subtract({ days: date.dayOfWeek - 1 })
}

/** Returns the Sunday of the ISO week containing the given date. */
export function getEndOfWeek (date: Temporal.PlainDate): Temporal.PlainDate {
  return getStartOfWeek(date).add({ days: 6 })
}

/** Returns the full ISO week range (Monday–Sunday) for the given date. */
export function getPlainDateWeekRange (date: Temporal.PlainDate): PlainDateRange {
  const begin = getStartOfWeek(date)

  return {
    begin,
    until: getEndOfWeek(date)
  }
}
